import cn from 'clsx';
import LogoSymbol from '../LogoSymbol';
import { useRef, useEffect } from 'react';
import { IconButton } from '@v2/brands/discountmags/Layouts/Full/Navigation';
import useToggle, { ToggleState } from '@v2/shared/lib/useToggle';
import { type SearchBarState, type MenuItem, type Category } from '@v2/shared/lib/types';
import { SearchAutoCompleteProps } from '@v2/shared/lib/useSearchAutoComplete';
import { faBars, faUserCircle, faSearch } from '@fortawesome/pro-light-svg-icons';

import MainMenu from './MainMenu';
import SearchBar from './SearchBar';
import BackgroundOverlay from './BackgroundOverlay';
import { CartLink } from '../CartLink/CartLink';

type Props = {
  categories: Category[];
  featuredCategories: Category[];
  booksCategories?: Category[];
  featuredBooksCategories?: Category[];
  accountMenuState: ToggleState;
  searchBarState: SearchBarState;
  fixedHeaderVisible: boolean;
  /** Null while the cart has not loaded. */
  cartCount: number | null;
  specialDealLinks: MenuItem[];
  socialLinks: MenuItem[];
  cartUrl: string;
  homeUrl: string;
  searchAutoCompleteProps: SearchAutoCompleteProps;
};

export default function MobileNav({
  categories,
  featuredCategories,
  booksCategories,
  featuredBooksCategories,
  accountMenuState,
  searchBarState,
  fixedHeaderVisible,
  cartCount,
  specialDealLinks,
  socialLinks,
  cartUrl,
  homeUrl,
  searchAutoCompleteProps,
}: Props) {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const alwaysShowSearch = !fixedHeaderVisible;
  const { isOpen: showMainMenu, toggle: toggleMainMenu, open: openMenu, close: closeMainMenu } = useToggle();

  useEffect(() => {
    if (alwaysShowSearch) {
      searchBarState.close();
      searchBarState.unfocus();
    } else {
      searchBarState.unfocus();
    }
  }, [alwaysShowSearch]);

  useEffect(() => {
    if (fixedHeaderVisible && searchInputRef.current) {
      searchInputRef.current.focus();
    } else {
      searchInputRef.current?.blur();
    }
  }, [searchBarState.isOpen, searchInputRef, fixedHeaderVisible]);

  return (
    <>
      {/*
       * Same name as the desktop bar's. They are the one primary navigation at two widths
       * and only ever one is in the accessibility tree — the other is display:none — so a
       * second name would describe a distinction a reader never encounters.
       */}
      <nav aria-label="Primary" className="bg-white md:hidden border-b-[1.5px] border-slate-200">
        <div className="flex items-stretch justify-between">
          <div className="flex flex-1 items-center">
            <IconButton icon={faBars} onClick={openMenu} ariaLabel="Menu" className="p-2" />
            {fixedHeaderVisible && (
              <IconButton
                icon={faSearch}
                ariaLabel="Search"
                onClick={searchBarState.toggle}
                className={cn('p-2 pl-1')}
                iconClassName="text-xl"
              />
            )}
          </div>
          <div className="flex items-center">
            <a href={homeUrl} aria-label="Navigate home" className="inline-flex px-3 items-center">
              <LogoSymbol />
            </a>
          </div>
          <div className="flex flex-1 justify-end items-center">
            <IconButton icon={faUserCircle} className="p-2" onClick={accountMenuState.toggle} ariaLabel="Account" />
            <CartLink isMobile cartCount={cartCount} cartUrl={cartUrl} />
          </div>
        </div>
        <SearchBar
          isOpen={alwaysShowSearch ? alwaysShowSearch : searchBarState.isOpen}
          searchBarState={searchBarState}
          searchAutoCompleteProps={searchAutoCompleteProps}
          ref={searchInputRef}
        />
      </nav>
      <MainMenu
        isOpen={showMainMenu}
        toggle={toggleMainMenu}
        close={closeMainMenu}
        categories={categories}
        featuredCategories={featuredCategories}
        booksCategories={booksCategories}
        featuredBooksCategories={featuredBooksCategories}
        specialDealLinks={specialDealLinks}
        socialLinks={socialLinks}
      />
      <BackgroundOverlay isOpen={searchBarState.isFocused} />
    </>
  );
}
